import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { X, Plus, Minus, AlertTriangle, Loader, FileText, Key, AlertCircle, Search, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Button from '../common/Button';
import ClientWarningCard from './ClientWarningCard';
import { bulkContractService } from '../../services/bulkContractService';

const BulkContractCreateModal = ({
    isOpen,
    onClose,
    onSuccess,
    clients = [],
    services = [],
    templates = []
}) => {
    const [selectedClients, setSelectedClients] = useState([]);
    const [clientWarnings, setClientWarnings] = useState({});
    const [loadingWarnings, setLoadingWarnings] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCounties, setExpandedCounties] = useState({});

    const {
        register,
        handleSubmit,
        control,
        reset,
        watch,
        formState: { errors }
    } = useForm({
        defaultValues: {
            templateId: '',
            numberPrefix: 'CNT',
            startDate: new Date().toISOString().split('T')[0],
            dueDate: `${new Date().getFullYear()}-12-31`,
            description: '',
            autoGeneratePdf: true,
            sendToClients: false,
            generateAccessCodes: true,
            services: [{ serviceId: '', quantity: 1, customPrice: '' }]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'services'
    });

    // Group clients by county and filter by search
    const groupedClients = useMemo(() => {
        const filtered = clients.filter(client =>
            client.name.toLowerCase().includes(searchQuery.toLowerCase())
        );

        const groups = filtered.reduce((acc, client) => {
            const county = client.county || 'Fără județ';
            if (!acc[county]) {
                acc[county] = [];
            }
            acc[county].push(client);
            return acc;
        }, {});

        // Sort counties alphabetically
        return Object.keys(groups)
            .sort()
            .reduce((acc, county) => {
                acc[county] = groups[county].sort((a, b) => 
                    a.name.localeCompare(b.name)
                );
                return acc;
            }, {});
    }, [clients, searchQuery]);

    // Initialize expanded counties (all expanded by default)
    useEffect(() => {
        const initialExpanded = Object.keys(groupedClients).reduce((acc, county) => {
            acc[county] = true;
            return acc;
        }, {});
        setExpandedCounties(initialExpanded);
    }, [groupedClients]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedClients([]);
            setClientWarnings({});
            setSearchQuery('');
            reset();
        }
    }, [isOpen, reset]);

    useEffect(() => {
        if (selectedClients.length > 0) {
            checkClientWarnings();
        } else {
            setClientWarnings({});
        }
    }, [selectedClients]);

    const checkClientWarnings = async () => {
        setLoadingWarnings(true);
        try {
            const warnings = await bulkContractService.checkMultipleClientWarnings(selectedClients);
            setClientWarnings(warnings);
        } catch (error) {
            console.error('Error checking client warnings:', error);
        } finally {
            setLoadingWarnings(false);
        }
    };

    const handleClientSelection = (clientId, checked) => {
        if (checked) {
            setSelectedClients(prev => [...prev, clientId]);
        } else {
            setSelectedClients(prev => prev.filter(id => id !== clientId));
        }
    };

    const handleSelectAllClients = (checked) => {
        if (checked) {
            const visibleClientIds = Object.values(groupedClients)
                .flat()
                .map(c => c.id);
            setSelectedClients(visibleClientIds);
        } else {
            setSelectedClients([]);
        }
    };

    const handleCountySelection = (county, checked) => {
        const countyClientIds = groupedClients[county].map(c => c.id);
        
        if (checked) {
            setSelectedClients(prev => {
                const newSet = new Set([...prev, ...countyClientIds]);
                return Array.from(newSet);
            });
        } else {
            setSelectedClients(prev => 
                prev.filter(id => !countyClientIds.includes(id))
            );
        }
    };

    const toggleCounty = (county) => {
        setExpandedCounties(prev => ({
            ...prev,
            [county]: !prev[county]
        }));
    };

    const isCountyFullySelected = (county) => {
        const countyClientIds = groupedClients[county].map(c => c.id);
        return countyClientIds.every(id => selectedClients.includes(id));
    };

    const isCountyPartiallySelected = (county) => {
        const countyClientIds = groupedClients[county].map(c => c.id);
        const selectedCount = countyClientIds.filter(id => selectedClients.includes(id)).length;
        return selectedCount > 0 && selectedCount < countyClientIds.length;
    };

    const onSubmit = async (data) => {
        if (selectedClients.length === 0) {
            toast.error('Selectați cel puțin un client');
            return;
        }

        setLoading(true);
        try {
            const bulkData = {
                clientIds: selectedClients,
                templateId: data.templateId ? parseInt(data.templateId) : null,
                numberPrefix: data.numberPrefix,
                startDate: data.startDate,
                dueDate: data.dueDate,
                description: data.description,
                autoGeneratePdf: data.autoGeneratePdf,
                sendToClients: data.sendToClients,
                generateAccessCodes: data.generateAccessCodes,
                services: data.services.map(s => ({
                    serviceId: parseInt(s.serviceId),
                    quantity: parseInt(s.quantity),
                    customPrice: s.customPrice ? parseFloat(s.customPrice) : null
                }))
            };

            await bulkContractService.createBulkContracts(bulkData);
            toast.success(`${selectedClients.length} contracte create cu succes!`);
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating bulk contracts:', error);
            toast.error(error.response?.data?.message || 'Eroare la crearea contractelor în bulk');
        } finally {
            setLoading(false);
        }
    };

    const hasWarnings = Object.values(clientWarnings).some(w => w?.hasWarnings);
    const totalVisibleClients = Object.values(groupedClients).flat().length;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Creare Contracte în Bulk"
            size="xl"
        >
            <div onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Client Selection */}
                <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">
                            Selectare Clienți
                        </h3>
                        <div className="flex items-center space-x-4">
                            <label className="flex items-center text-sm text-gray-600">
                                <input
                                    type="checkbox"
                                    checked={selectedClients.length === totalVisibleClients && totalVisibleClients > 0}
                                    onChange={(e) => handleSelectAllClients(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                                />
                                Selectează Toți
                            </label>
                            <span className="text-sm font-medium text-gray-700">
                                {selectedClients.length} / {clients.length}
                            </span>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="mb-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Caută client după nume..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* Grouped Clients */}
                    <div className="max-h-96 overflow-y-auto space-y-2 border border-gray-200 rounded p-3">
                        {Object.keys(groupedClients).length === 0 ? (
                            <p className="text-center text-gray-500 py-4">
                                {searchQuery ? 'Niciun client găsit' : 'Nu există clienți'}
                            </p>
                        ) : (
                            Object.entries(groupedClients).map(([county, countyClients]) => (
                                <div key={county} className="border border-gray-200 rounded-lg overflow-hidden">
                                    {/* County Header */}
                                    <div className="bg-gray-50 border-b border-gray-200">
                                        <div className="flex items-center justify-between p-2">
                                            <button
                                                type="button"
                                                onClick={() => toggleCounty(county)}
                                                className="flex items-center flex-1 text-left hover:bg-gray-100 rounded px-2 py-1 transition-colors"
                                            >
                                                {expandedCounties[county] ? (
                                                    <ChevronDown className="h-4 w-4 mr-2 text-gray-500" />
                                                ) : (
                                                    <ChevronUp className="h-4 w-4 mr-2 text-gray-500" />
                                                )}
                                                <span className="font-medium text-gray-900">
                                                    {county}
                                                </span>
                                                <span className="ml-2 text-sm text-gray-500">
                                                    ({countyClients.length})
                                                </span>
                                            </button>
                                            <label 
                                                className="flex items-center px-2 cursor-pointer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isCountyFullySelected(county)}
                                                    ref={input => {
                                                        if (input) {
                                                            input.indeterminate = isCountyPartiallySelected(county);
                                                        }
                                                    }}
                                                    onChange={(e) => handleCountySelection(county, e.target.checked)}
                                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-600">
                                                    Selectează tot
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* County Clients */}
                                    {expandedCounties[county] && (
                                        <div className="divide-y divide-gray-100">
                                            {countyClients.map((client) => (
                                                <label
                                                    key={client.id}
                                                    className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                                                        selectedClients.includes(client.id) ? 'bg-blue-50' : ''
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedClients.includes(client.id)}
                                                        onChange={(e) => handleClientSelection(client.id, e.target.checked)}
                                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded flex-shrink-0"
                                                    />
                                                    <div className="ml-3 flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {client.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            CIF: {client.fiscalCode}
                                                        </p>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Warnings */}
                {loadingWarnings && (
                    <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                        <Loader className="h-5 w-5 animate-spin text-blue-600 mr-2" />
                        <span className="text-sm text-gray-600">Verificare date clienți...</span>
                    </div>
                )}

                {hasWarnings && !loadingWarnings && (
                    <div className="space-y-2">
                        <div className="flex items-center text-yellow-700 mb-2">
                            <AlertTriangle className="h-5 w-5 mr-2" />
                            <span className="text-sm font-medium">Atenționări pentru clienți selectați:</span>
                        </div>
                        <div className="max-h-60 overflow-y-auto space-y-2">
                            {Object.entries(clientWarnings).map(([clientId, warning]) => (
                                <ClientWarningCard key={clientId} warning={warning} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Contract Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Template Contract
                        </label>
                        <select
                            {...register('templateId')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Fără template</option>
                            {templates.map((template) => (
                                <option key={template.id} value={template.id}>
                                    {template.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Prefix Număr
                        </label>
                        <input
                            type="text"
                            {...register('numberPrefix')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="CNT"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Data Început *
                        </label>
                        <input
                            type="date"
                            {...register('startDate', { required: 'Data început este obligatorie' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.startDate && (
                            <p className="text-red-500 text-xs mt-1">{errors.startDate.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Data Expirare *
                        </label>
                        <input
                            type="date"
                            {...register('dueDate', { required: 'Data expirare este obligatorie' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.dueDate && (
                            <p className="text-red-500 text-xs mt-1">{errors.dueDate.message}</p>
                        )}
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descriere (opțional)
                    </label>
                    <textarea
                        {...register('description')}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Descriere pentru acest bulk de contracte..."
                    />
                </div>

                {/* Services */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                            Servicii *
                        </label>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            icon={Plus}
                            onClick={() => append({ serviceId: '', quantity: 1, customPrice: '' })}
                        >
                            Adaugă Serviciu
                        </Button>
                    </div>

                    <div className="space-y-3">
                        {fields.map((field, index) => (
                            <div key={field.id} className="flex items-center space-x-3">
                                <div className="flex-1">
                                    <select
                                        {...register(`services.${index}.serviceId`, {
                                            required: 'Serviciul este obligatoriu'
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Selectează serviciu</option>
                                        {services.map((service) => (
                                            <option key={service.id} value={service.id}>
                                                {service.name} - {service.price} {service.currency}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="w-24">
                                    <input
                                        type="number"
                                        min="1"
                                        {...register(`services.${index}.quantity`)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Cant."
                                    />
                                </div>

                                <div className="w-28">
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register(`services.${index}.customPrice`)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Preț"
                                    />
                                </div>

                                {fields.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="p-2 text-red-600 hover:text-red-800"
                                    >
                                        <Minus className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Options */}
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                        <FileText className="h-4 w-4 mr-2" />
                        Opțiuni
                    </h3>

                    <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                        <label className="flex items-start">
                            <input
                                type="checkbox"
                                {...register('autoGeneratePdf')}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded mt-1"
                            />
                            <div className="ml-2">
                                <span className="text-sm font-medium text-gray-700">
                                    Generează automat PDF-urile
                                </span>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Contractele vor fi generate imediat după creare
                                </p>
                            </div>
                        </label>

                        <label className="flex items-start">
                            <input
                                type="checkbox"
                                {...register('generateAccessCodes')}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded mt-1"
                            />
                            <div className="ml-2 flex-1">
                                <div className="flex items-center">
                                    <span className="text-sm font-medium text-gray-700">
                                        Generează coduri de acces
                                    </span>
                                    <Key className="h-3.5 w-3.5 ml-1.5 text-blue-600" />
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Fiecare contract va primi automat un cod de acces securizat (valabil 48 ore)
                                </p>
                            </div>
                        </label>

                        <label className="flex items-start">
                            <input
                                type="checkbox"
                                {...register('sendToClients')}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded mt-1"
                            />
                            <div className="ml-2">
                                <span className="text-sm font-medium text-gray-700">
                                    Trimite automat contractele către clienți
                                </span>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Email-urile vor fi trimise după generarea contractelor
                                    {watch('generateAccessCodes') && ' (include link și cod de acces)'}
                                </p>
                            </div>
                        </label>
                    </div>

                    {(watch('autoGeneratePdf') || watch('generateAccessCodes')) && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-start">
                                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                                <div className="text-xs text-blue-800">
                                    <p className="font-medium mb-1">Se vor executa automat:</p>
                                    <ul className="list-disc list-inside space-y-0.5">
                                        {watch('autoGeneratePdf') && (
                                            <li>Generarea PDF-urilor pentru toate contractele</li>
                                        )}
                                        {watch('generateAccessCodes') && (
                                            <li>Generarea codurilor de acces securizate (valabile 48 ore)</li>
                                        )}
                                        {watch('sendToClients') && (
                                            <li>Trimiterea email-urilor către clienți cu linkuri și coduri</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Anulează
                    </Button>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        loading={loading}
                        disabled={loading || selectedClients.length === 0}
                    >
                        Creează {selectedClients.length} Contract{selectedClients.length !== 1 ? 'e' : ''}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default BulkContractCreateModal;